import {
  addCalendarDays,
  parseDateOnly,
  requireDateOnlyString,
  type DateOnlyString,
} from "../../utils/dateOnly";

export type PortalDatePreset = "all" | "week" | "month" | "year" | "custom";

export type PortalDateRange = [DateOnlyString | null, DateOnlyString | null];

export interface PortalDateFilterRange {
  readonly from?: DateOnlyString;
  readonly to?: DateOnlyString;
}

export function buildPortalDateFilterRange(
  preset: string,
  customRange: PortalDateRange,
  labToday: DateOnlyString
): PortalDateFilterRange {
  switch (preset as PortalDatePreset) {
    case "week":
      return buildTrailingRange(labToday, 6);
    case "month":
      return buildTrailingRange(labToday, 29);
    case "year":
      return {
        from: requireDateOnlyString(`${labToday.slice(0, 4)}-01-01`),
        to: labToday,
      };
    case "custom":
      return customRange[0] && customRange[1]
        ? { from: customRange[0], to: customRange[1] }
        : {};
    case "all":
    default:
      return {};
  }
}

export function normalizePortalDatePickerRange(
  range: readonly [unknown, unknown]
): PortalDateRange {
  const from = normalizePortalDatePickerValue(range[0]);
  const to = normalizePortalDatePickerValue(range[1]);
  const hasInvalidValue =
    (range[0] !== null && !from) || (range[1] !== null && !to);

  return hasInvalidValue ? [null, null] : [from ?? null, to ?? null];
}

function normalizePortalDatePickerValue(
  value: unknown
): DateOnlyString | undefined {
  if (!(value instanceof Date)) return parseDateOnly(value);
  if (Number.isNaN(value.getTime())) return undefined;

  return parseDateOnly(
    `${padYear(value.getFullYear())}-${padTwoDigits(
      value.getMonth() + 1
    )}-${padTwoDigits(value.getDate())}`
  );
}

function buildTrailingRange(
  labToday: DateOnlyString,
  precedingDays: number
): PortalDateFilterRange {
  const from = addCalendarDays(labToday, -precedingDays);
  if (!from)
    throw new Error("Portal date preset is outside the supported range");
  return { from, to: labToday };
}

function padTwoDigits(value: number): string {
  return String(value).padStart(2, "0");
}

function padYear(value: number): string {
  return String(value).padStart(4, "0");
}
